import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Package2 } from 'lucide-react';

export const AcceptInvite = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) { setError('Missing invitation token.'); return; }
      try {
        const res = await api.get(`/auth/invite/${token}`);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation is invalid or expired.');
      }
    };
    load();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 12) { toast.error('Password must be at least 12 characters, with upper and lower case letters, a number and a symbol'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/accept-invite', { token, name: name.trim(), password });
      // Sign-in continues on the login page: authenticator app setup.
      toast.success(res.data?.message || 'Account created. Sign in to finish setting up.');
      navigate('/login', { state: { email: res.data?.email } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/30 to-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-4">
            <Package2 className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold font-[Manrope] text-foreground tracking-tight">Furniva CRM</h1>
          <p className="text-muted-foreground mt-2">Accept your invitation</p>
        </div>
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="font-[Manrope]">Create your account</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : !invite ? (
              <p className="text-sm text-muted-foreground">Verifying invitation…</p>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  You've been invited as <b className="text-primary capitalize">{invite.role}</b> for
                  <b className="ml-1 text-foreground">{invite.email}</b>.
                </p>
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
                </div>
                <div className="space-y-2">
                  <Label>Confirm password</Label>
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Creating account…' : 'Activate account'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
